import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronsUpDown } from "lucide-react";
import {
  Button,
  SearchableCombobox,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
  type ComboboxOptionGroup,
} from "@nexus/ui";
import type { TenantPermissions } from "@/features/auth/lib/api";
import { deriveUserRole } from "@/lib/analytics-utils";
import { useRecentTenants } from "@/features/auth/hooks/useRecentTenants";
import { OrgLogoMark } from "@/features/identity-hub/components/dashboard/OrgLogoMark";
import {
  ORG_PICKER_ITEM_PLAIN_CLASS,
  ORG_PICKER_POPOVER_CLASS,
  OrgPickerOptionContent,
} from "@/features/identity-hub/components/dashboard/org-picker-shared";

interface TenantSelectorProps {
  tenants: TenantPermissions[];
  activeTenantId: string | null;
  onSelect: (tenantId: string) => void;
  collapsed: boolean;
  onExpandRequest: () => void;
}

export function TenantSelector({
  tenants,
  activeTenantId,
  onSelect,
  collapsed,
  onExpandRequest,
}: TenantSelectorProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  // Radix click-outside doesn't fire for clicks inside iframes (separate browsing context).
  // Closing on window blur catches focus moving into the Retool iframe.
  useEffect(() => {
    const close = () => {
      if (document.activeElement?.tagName === 'IFRAME') setOpen(false);
    };
    window.addEventListener("blur", close);
    return () => window.removeEventListener("blur", close);
  }, []);

  const sortedTenants = useMemo(
    () => [...tenants].sort((a, b) => a.name.localeCompare(b.name)),
    [tenants],
  );

  const { recent, record } = useRecentTenants(tenants, activeTenantId);

  const groups = useMemo<ComboboxOptionGroup<TenantPermissions>[]>(() => {
    if (recent.length === 0) {
      return [{ items: sortedTenants }];
    }
    const recentIds = new Set(recent.map((r) => r.id));
    return [
      { heading: t("sidebar.recentTenants"), items: recent },
      {
        heading: t("sidebar.allTenants"),
        items: sortedTenants.filter((tenant) => !recentIds.has(tenant.id)),
      },
    ];
  }, [recent, sortedTenants, t]);

  if (tenants.length === 0) return null;

  const activeTenant =
    sortedTenants.find((tenant) => tenant.id === activeTenantId) ??
    sortedTenants[0];

  if (tenants.length === 1) {
    if (collapsed) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex justify-center px-0 py-1">
              <OrgLogoMark
                name={activeTenant.name}
                logoUrl={activeTenant.logoUrl}
                isSuperTenant={activeTenant.type === "SUPER_TENANT"}
                neutral
                size={24}
              />
            </div>
          </TooltipTrigger>
          <TooltipContent side="right">{activeTenant.name}</TooltipContent>
        </Tooltip>
      );
    }
    return (
      <div className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-normal text-[var(--sidebar-foreground)]">
        <OrgLogoMark
          name={activeTenant.name}
          logoUrl={activeTenant.logoUrl}
          isSuperTenant={activeTenant.type === "SUPER_TENANT"}
          neutral
          size={24}
        />
        <span className="truncate">{activeTenant.name}</span>
      </div>
    );
  }

  if (collapsed) {
    return (
      <Button
        variant="ghost"
        size="icon"
        className="mx-auto h-8 w-8 p-0"
        onClick={onExpandRequest}
        aria-label={t("sidebar.switchTenant")}
        title={t("sidebar.switchTenant")}
      >
        <OrgLogoMark
          name={activeTenant.name}
          logoUrl={activeTenant.logoUrl}
          isSuperTenant={activeTenant.type === "SUPER_TENANT"}
          neutral
          size={24}
        />
      </Button>
    );
  }

  return (
    <SearchableCombobox<TenantPermissions>
      groups={groups}
      value={activeTenantId}
      open={open}
      onOpenChange={setOpen}
      onSelect={(tenant) => {
        record(tenant.id);
        onSelect(tenant.id);
      }}
      getKey={(tenant) => tenant.id}
      getLabel={(tenant) => tenant.name}
      placeholder={t("sidebar.switchTenant")}
      ariaLabel={t("sidebar.switchTenant")}
      searchPlaceholder={t("sidebar.searchTenants")}
      emptyMessage={t("sidebar.noTenantsMatch")}
      contentClassName={ORG_PICKER_POPOVER_CLASS}
      itemClassName={ORG_PICKER_ITEM_PLAIN_CLASS}
      renderItem={(tenant) => {
        const role = deriveUserRole(tenant.permissions);
        return (
          <OrgPickerOptionContent
            org={tenant}
            showTypeBadge={false}
            accent={false}
            roleLabel={
              tenant.roleName ?? t(`sidebar.role.${role}`, { admin: "Admin", editor: "Editor", viewer: "Viewer" }[role] ?? role)
            }
          />
        );
      }}
      renderTrigger={({ open: triggerOpen }) => (
        <button
          type="button"
          role="combobox"
          aria-expanded={triggerOpen}
          aria-label={t("sidebar.switchTenant")}
          className={cn(
            "flex h-9 w-full items-center gap-2 rounded-md px-2 text-sm outline-none transition-colors",
            "bg-[var(--sidebar-accent)] text-[var(--sidebar-foreground)] hover:bg-[var(--sidebar-accent)]/80",
            "focus-visible:ring-1 focus-visible:ring-ring/40",
            triggerOpen && "ring-1 ring-ring/40",
          )}
        >
          <OrgLogoMark
            name={activeTenant.name}
            logoUrl={activeTenant.logoUrl}
            isSuperTenant={activeTenant.type === "SUPER_TENANT"}
            neutral
            size={20}
          />
          <span className="min-w-0 flex-1 truncate text-left">
            {activeTenant.name}
          </span>
          <ChevronsUpDown className="size-3.5 shrink-0 opacity-50" aria-hidden />
        </button>
      )}
    />
  );
}
